import CheckCircle from "@mui/icons-material/CheckCircle";
import { Box, CircularProgress, TextField, Typography, useTheme } from "@mui/material";
import { useDebounce } from "hooks/useDebounce";
import React, { useEffect, useRef, useState } from "react";

export interface AutosaveFormTextFieldProps {
  onSave: (value: string) => Promise<void>;
  delay?: number;
  showSaveIndicator?: boolean;
  clearSavedIndicatorAfterDelay?: boolean;
  defaultValue?: string;
  label?: string;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  fullWidth?: boolean;
  disabled?: boolean;
  /**
   * Drop the box's horizontal padding so the label/text sit flush-left, aligning
   * with adjacent unpadded fields (e.g. the drawer's Details grid).
   */
  disableGutters?: boolean;
}

export function AutosaveFormTextField({
  onSave,
  delay = 1000,
  showSaveIndicator = true,
  clearSavedIndicatorAfterDelay = false,
  defaultValue = "",
  label,
  placeholder,
  multiline = false,
  rows = 1,
  fullWidth = true,
  disabled = false,
  disableGutters = false,
}: AutosaveFormTextFieldProps) {
  const theme = useTheme();
  const [value, setValue] = useState<string>(defaultValue);
  const [lastSavedValue, setLastSavedValue] = useState<string>(defaultValue);
  // Tracks whether the user is actively editing, so an external `defaultValue`
  // change never clobbers in-progress typing. State (not a ref) so blur triggers
  // the re-render that applies a deferred external change.
  const [isFocused, setIsFocused] = useState(false);
  // Adjust state during render (not in an effect) when `defaultValue` changes
  // externally — e.g. saved from the edit dialog — so the field reflects it
  // immediately instead of showing stale text until a reload.
  const [prevDefaultValue, setPrevDefaultValue] = useState<string>(defaultValue);
  if (!isFocused && defaultValue !== prevDefaultValue) {
    // Only adopt the external value when there are no unsaved local edits, and
    // only advance prevDefaultValue when we actually adopt. If `value` differs
    // from `lastSavedValue` the user's in-flight text is newer than the
    // echoed-back defaultValue — resyncing would discard it. Leaving
    // prevDefaultValue stale keeps the change *pending* (the guard re-fires on a
    // later render) so it applies once the edit settles, rather than being
    // dropped permanently.
    if (value === lastSavedValue) {
      setPrevDefaultValue(defaultValue);
      setValue(defaultValue);
      setLastSavedValue(defaultValue);
    }
  }
  const [showSavedIndicator, setShowSavedIndicator] = useState(false);
  const [showPendingIndicator, setShowPendingIndicator] = useState(false);
  const pendingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [debouncedValue] = useDebounce(value, delay);

  // The pending timer is cleared on the next keystroke and when a save starts,
  // but nothing cleared it on unmount, so a field unmounted within 250ms of a
  // keystroke still fired `setShowPendingIndicator` afterwards. Under vitest
  // that lands after the jsdom environment is torn down and throws
  // `ReferenceError: window is not defined` out of react-dom, which fails the
  // whole run from a passing test file.
  useEffect(() => {
    return () => {
      if (pendingTimerRef.current) {
        clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
    };
  }, []);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    setShowSavedIndicator(false);

    // Clear existing timer
    if (pendingTimerRef.current) {
      clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }

    // Show pending indicator after 250ms only if the value differs from what we
    // last persisted. `lastSavedValue` (not `defaultValue`) is the single
    // baseline: editing back to the server's current value while lastSavedValue
    // is stale still needs a save, so it must still read as pending.
    if (newValue !== lastSavedValue) {
      const timer = setTimeout(() => {
        setShowPendingIndicator(true);
      }, 250);
      pendingTimerRef.current = timer;
    } else {
      // Clear pending indicator but dont save (on the backend) if the user types
      // but the value is the same as the last saved value
      setShowPendingIndicator(false);
      setShowSavedIndicator(true);

      if (clearSavedIndicatorAfterDelay) {
        setTimeout(() => setShowSavedIndicator(false), delay);
      }
    }
  };

  // Save after debounce delay
  useEffect(() => {
    const save = async () => {
      // `debouncedValue === value` gates out a stale in-flight debounce: if an
      // external save updated the field (via the render-phase sync) while a
      // debounce from earlier typing was pending, the lagging debouncedValue
      // would otherwise fire onSave with the old text and clobber it.
      // `lastSavedValue` is the sole server baseline — comparing against
      // `defaultValue` too let lastSavedValue drift out of sync and fire a stale
      // save over a newer external value.
      if (debouncedValue === value && debouncedValue !== lastSavedValue) {
        // Clear pending timer immediately when starting save
        if (pendingTimerRef.current) {
          clearTimeout(pendingTimerRef.current);
          pendingTimerRef.current = null;
        }

        try {
          await onSave(debouncedValue);
          setLastSavedValue(debouncedValue);
          setShowPendingIndicator(false);
          setShowSavedIndicator(true);

          if (clearSavedIndicatorAfterDelay) {
            setTimeout(() => setShowSavedIndicator(false), delay);
          }
        } catch (error) {
          console.error("Failed to save value:", error);
          setShowPendingIndicator(false);
        }
      }
    };
    save();
  }, [debouncedValue, value, lastSavedValue, onSave, delay, clearSavedIndicatorAfterDelay]);

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.grey50.main,
        px: disableGutters ? 0 : 2,
        py: 0.5,
        borderRadius: 1,
        minHeight: 50,
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      {/* Label */}
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          fontSize: "0.75rem",
          lineHeight: 1.5,
        }}
      >
        {label}
      </Typography>

      {/* Input Field */}
      <TextField
        value={value}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        multiline={multiline}
        minRows={rows}
        maxRows={multiline ? 4 : 1}
        fullWidth={fullWidth}
        disabled={disabled}
        variant="standard"
        slotProps={{
          input: {
            disableUnderline: true,
          },
        }}
        sx={{
          margin: 0,
          "& .MuiInput-root": {
            fontSize: "1rem",
            color: "text.primary",
            alignItems: "flex-start",
            mt: 0,
            pt: 0,
            pb: 0,
          },
          "& .MuiInput-root:before": {
            borderBottom: "none",
          },
          "& .MuiInput-root:after": {
            borderBottom: "none",
          },
          "& .MuiInput-root:hover:before": {
            borderBottom: "none",
          },
          "& .MuiInputBase-input": {
            padding: 0,
            wordBreak: "break-word",
            whiteSpace: multiline ? "pre-wrap" : "nowrap",
            overflow: multiline ? "visible" : "hidden",
            textOverflow: multiline ? "clip" : "ellipsis",
            resize: "none",
            lineHeight: 1.43,
            minHeight: "1rem",
          },
        }}
      />

      {/* Save Indicators */}
      {showSaveIndicator && (
        <Box
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {showPendingIndicator && <CircularProgress size={16} />}
          {showSavedIndicator && (
            <CheckCircle
              sx={{
                color: "success.main",
                fontSize: 16,
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
